import React, { useEffect, useRef, type PropsWithChildren } from 'react';

const modifierAliases: { [key: string]: string } = {
  ctrl: 'control',
  cmd: 'meta',
  command: 'meta',
  option: 'alt',
  opt: 'alt',
  alternate: 'alt',
  shft: 'shift',
};

/**
 * Normalizes a keyboard event into a standardized string representation
 *
 * Converts a KeyboardEvent into a consistent string format (e.g., "control+shift+a")
 * with modifiers in a standardized order regardless of the sequence pressed.
 *
 * Examples:
 * ```js
 *   normalizeKeyEvent({key: 'A', shiftKey: true, ctrlKey: true}) → "control+shift+a"
 *   normalizeKeyEvent({key: 'Enter'}) → "enter"
 *   normalizeKeyEvent({key: 'Shift', shiftKey: true}) → "shift"
 * ```
 */
function normalizeKeyEvent(event: KeyboardEvent, ignoreModifiers = false) {
  const modifierOrder = ['control', 'shift', 'alt', 'meta'];
  const modifiers = [];

  if (ignoreModifiers !== true) {
    if (event.ctrlKey) modifiers.push('control');
    if (event.shiftKey) modifiers.push('shift');
    if (event.altKey) modifiers.push('alt');
    if (event.metaKey) modifiers.push('meta');
  }

  const key = event.key.toLowerCase();
  if (!modifierOrder.includes(key)) {
    modifiers.push(key);
  }

  return modifiers.join('+').toLowerCase();
}

/**
 * Normalizes a keyboard shortcut string into a standardized format
 *
 * Converts a string representation of a key combination (e.g., "shift+ctrl+a") into a
 * consistent format with modifiers in a standardized order regardless of input order.
 * Handles modifier key aliases and maintains consistent ordering.
 *
 * Examples:
 * ```js
 *   normalizeKeySequence("shift+ctrl+a") → "control+shift+a"
 *   normalizeKeySequence("meta+s") → "meta+s"
 *   normalizeKeySequence("alt+shift+enter") → "shift+alt+enter"
 * ```
 */
function normalizeKeySequence(combo: string) {
  const modifierOrder = ['control', 'shift', 'alt', 'meta'];
  const keys = combo.toLowerCase().split('+');

  const modifiers = keys
    .filter((key) => modifierOrder.includes(modifierAliases[key] || key))
    .map((key) => modifierAliases[key] || key);
  const mainKeys = keys.filter(
    (key) => !modifierOrder.includes(modifierAliases[key] || key),
  );

  modifiers.sort((a, b) => modifierOrder.indexOf(a) - modifierOrder.indexOf(b));

  return [...modifiers, ...mainKeys].join('+');
}

/**
 * Normalizes an array of hotkey combo definitions
 *
 * Processes an array of hotkey definitions, ensuring all key sequences are normalized
 * to a consistent format. Handles both single key sequences and arrays of sequences.
 * Preserves the callback function and configuration for each hotkey.
 *
 * Example:
 * ```js
 *   normalizeHotkeyCombos([
 *     ["ctrl+s", saveFunction, {preventDefault: true}],
 *     [["meta+c", "ctrl+c"], copyFunction]
 *   ])
 *   → [
 *       [["control+s"], saveFunction, {preventDefault: true}],
 *       [["meta+c", "control+c"], copyFunction]
 *     ]
 * ```
 */
function normalizeHotkeyCombos(combos: HotkeyCombos): NormalizedHotkeyCombos {
  return combos.map(([keySequences, callback, config]) => {
    const normalizedKeySequences = (
      Array.isArray(keySequences) ? keySequences : [keySequences]
    ).map(normalizeKeySequence);

    return [
      normalizedKeySequences,
      callback,
      config,
    ] as NormalizedHotkeyCombos[number];
  });
}

export type HotkeyComboConfig = {
  /** Determines whether the hotkey is active and can trigger its callback. */
  enabled: boolean;

  /** If true, the hotkey will be triggered regardless of modifier keys (Ctrl, Shift, Alt, Meta). */
  ignoreModifiers?: boolean;

  /** If true, disables the default behavior of calling `event.preventDefault()`, allowing the browser's default action for the key. */
  allowDefault?: boolean;

  /** If true, disables the default behavior of calling `event.stopPropagation()`, allowing the event to bubble up the DOM. */
  allowPropagation?: boolean;

  /** If true, disables the default behavior of calling `event.stopImmediatePropagation()`, allowing event listeners
   * (for the same event that have not yet fired) run.
   *
   * **Note**
   * _This does not affect the callbacks defined in the other
   * hotkey handlers. This only applies to other keydown listeners on the dom outside of this component._ */
  allowImmediatePropagation?: boolean;
};

export type HotkeyCombos = [
  combos: string | string[],
  callback: (e: KeyboardEvent) => void,
  config: HotkeyComboConfig,
][];

type NormalizedHotkeyCombos = [
  string[],
  HotkeyCombos[number][1],
  HotkeyCombos[number][2],
][];

export type HotkeysProps = PropsWithChildren<{
  'data-testid'?: string;
  hotkeys: HotkeyCombos;
}>;

/**
 * Hotkeys Component
 *
 * This component captures and handles user-defined hotkeys within its child elements.
 * It listens for keydown events and triggers corresponding callback functions when
 * a matching hotkey is detected.
 *
 * Key Features:
 * - Captures defined hotkeys and triggers associated callbacks.
 * - Requires the user to supply an `enabled` flag in the config to ensure explicit control.
 * - Allows users to configure whether default actions, event propagation, and
 *   immediate propagation should be allowed or prevented.
 * - Users can manually handle the event in their callback function, but all callbacks
 *   share the same KeyboardEvent instance.
 *
 * Usage Examples:
 *
 * Example 1: Basic hotkey
 * ```js
 * const hotkeys = [
 *   ['a', (e) => console.log('Pressed A'), { enabled: true }]
 * ];
 * <Hotkeys hotkeys={hotkeys}>
 *   <input type="text" / >
 * </Hotkeys>
 * // Key pressed: 'a'
 * // Callback called: Yes
 * // preventDefault: Yes
 * // stopPropagation: Yes
 * // stopImmediatePropagation: Yes
 *
 * * // Key pressed: 'Control+a'
 * // Callback called: No
 * // preventDefault: No
 * // stopPropagation: No
 * // stopImmediatePropagation: No
 * ```
 *
 * Example 2: Hotkey with modifiers
 * ```js
 * const hotkeys = [
 *   ['control+shift+x', (e) => console.log('Conrol+Shift+X Pressed'), { enabled: true }]
 * ];
 * <Hotkeys hotkeys={hotkeys}>
 *   <input type="text" / >
 * </Hotkeys>
 * // Key pressed: 'Ctrl+Shift+x'
 * // Callback called: Yes
 * // preventDefault: Yes
 * // stopPropagation: Yes
 * // stopImmediatePropagation: Yes
 *
 * // Key pressed: 'Shift+x' or 'x'
 * // Callback called: No
 * // preventDefault: No
 * // stopPropagation: No
 * // stopImmediatePropagation: No
 * ```
 *
 * Example 3: Ignoring modifiers
 * ```js
 * const hotkeys = [
 *   ['a', (e) => console.log('Pressed A'), { enabled: true, ignoreModifiers: true }]
 * ];
 * <Hotkeys hotkeys={hotkeys}>
 *   <input type="text" / >
 * </Hotkeys>
 * // Key pressed: 'Control+A'
 * // Callback called: Yes
 * // preventDefault: Yes
 * // stopPropagation: Yes
 * // stopImmediatePropagation: Yes
 * ```
 *
 * Example 4: Allowing default browser behavior
 * ```js
 * const hotkeys = [
 *   ['s', (e) => console.log('Pressed S'), { enabled: true, allowDefault: true }]
 * ];
 * <Hotkeys hotkeys={hotkeys}>
 *   <input type="text" / >
 * </Hotkeys>
 * // Key pressed: 's'
 * // Callback called: Yes
 * // preventDefault: No
 * // stopPropagation: Yes
 * // stopImmediatePropagation: Yes
 * ```
 *
 * Example 5: Disabled hotkey
 * ```js
 * const hotkeys = [
 *   ['q', (e) => console.log('Pressed Q'), { enabled: false }]
 * ];
 * <Hotkeys hotkeys={hotkeys}>
 *   <input type="text" />
 * </Hotkeys>
 * // Key pressed: 'q'
 * // Callback called: No
 * // preventDefault: Yes
 * // stopPropagation: Yes
 * // stopImmediatePropagation: Yes
 * ```
 */
export function Hotkeys(props: HotkeysProps) {
  const ref = useRef<HTMLDivElement>(null);
  const hotkeysRef = useRef(normalizeHotkeyCombos(props.hotkeys));

  useEffect(() => {
    hotkeysRef.current = normalizeHotkeyCombos(props.hotkeys);
  }, [props.hotkeys]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!ref.current || !hotkeysRef.current) return;
      const hotkeys = hotkeysRef.current;

      if (
        ref.current.contains(document.activeElement) ||
        ref.current === document.activeElement
      ) {
        hotkeys.forEach(([keySequences, callback, config]) => {
          const {
            allowDefault = false,
            allowImmediatePropagation = false,
            allowPropagation = false,
          } = config;

          const keyInvoked = normalizeKeyEvent(e, config.ignoreModifiers);
          if (keySequences.includes(keyInvoked)) {
            if (config.enabled) {
              callback(e);
            }

            if (allowDefault !== true) {
              e.preventDefault();
            }

            if (allowImmediatePropagation !== true) {
              e.stopImmediatePropagation();
            }

            if (allowPropagation !== true) {
              e.stopPropagation();
            }
          }
        });
      }
    };

    // Use the capture phase to ensure that Hotkeys intercepts and processes events
    // before other event handlers in the app execute. This prevents unintended
    // interactions and allows outer hotkeys to explicitly permit events to propagate
    // down to inner, more specific hotkey handlers when necessary.
    document.addEventListener('keydown', handler, true);

    return () => {
      document.removeEventListener('keydown', handler, true);
    };
  }, []);

  return (
    <div data-testid={props['data-testid']} ref={ref}>
      {props.children}
    </div>
  );
}
